"use client";

import { c as _c } from "react/compiler-runtime";
import type { ActivityLogParams } from "@/components/profile-activity/ProfileActivityLogs";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import type { ApiIdentity } from "@/generated/models/ApiIdentity";
import type { ApiRepOverview } from "@/generated/models/ApiRepOverview";
import type { ApiRepCategoriesPage } from "@/generated/models/ApiRepCategoriesPage";
import type { ApiCicOverview } from "@/generated/models/ApiCicOverview";
import { commonApiFetch } from "@/services/api/common-api";
import { AuthContext } from "@/components/auth/Auth";
import { useSeizeConnectContext } from "@/components/auth/SeizeConnectContext";
import MobileWrapperDialog from "@/components/mobile-wrapper-dialog/MobileWrapperDialog";
import { RateMatter } from "@/types/enums";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useParams } from "next/navigation";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import UserPageIdentityHeader from "../identity/header/UserPageIdentityHeader";
import UserPageIdentityHeaderCICRate from "../identity/header/cic-rate/UserPageIdentityHeaderCICRate";
import UserPageIdentityStatements from "../identity/statements/UserPageIdentityStatements";
import UserPageRateWrapper from "../utils/rate/UserPageRateWrapper";
import UserPageCombinedActivityLog from "./UserPageCombinedActivityLog";
import type { RepDirection } from "./UserPageRep.helpers";
import { getCanEditNic } from "./UserPageRep.helpers";
import UserPageRepHeader from "./header/UserPageRepHeader";
import UserPageRepMobile from "./UserPageRepMobile";
const INITIAL_VISIBLE_CATEGORY_COUNT = 5;
const VISIBLE_CATEGORY_LOAD_STEP = 10;
const REP_CATEGORIES_PAGE_SIZE = 25;
const TOP_CONTRIBUTORS_LIMIT = 5;
const FIRST_REP_CATEGORIES_PAGE = 1;
const getDefaultVisibleCounts = (): Record<RepDirection, number> => ({
  received: INITIAL_VISIBLE_CATEGORY_COUNT,
  given: INITIAL_VISIBLE_CATEGORY_COUNT
});
const getDefaultFetchStates = (): Record<RepDirection, boolean> => ({
  received: false,
  given: false
});
function useRepCategories(t0) {
  const $ = _c(10);
  const {
    user,
    queryDirection,
    apiDirection,
    enabled
  } = t0;
  let t1;
  if ($[0] !== queryDirection || $[1] !== user) {
    t1 = [QueryKey.REP_CATEGORIES, {
      handleOrWallet: user,
      direction: queryDirection
    }];
    $[0] = queryDirection;
    $[1] = user;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== apiDirection || $[4] !== user) {
    t2 = async t3 => {
      const {
        pageParam
      } = t3;
      return await commonApiFetch({
        endpoint: `profiles/${user}/rep/categories`,
        params: {
          ...(apiDirection ? {
            direction: apiDirection
          } : {}),
          page: pageParam.toString(),
          page_size: REP_CATEGORIES_PAGE_SIZE.toString(),
          top_contributors_limit: TOP_CONTRIBUTORS_LIMIT.toString()
        }
      });
    };
    $[3] = apiDirection;
    $[4] = user;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== enabled || $[7] !== t1 || $[8] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2,
      initialPageParam: FIRST_REP_CATEGORIES_PAGE,
      getNextPageParam: _temp,
      enabled
    };
    $[6] = enabled;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return useInfiniteQuery(t3);
}
function _temp(lastPage) {
  return lastPage?.next ? lastPage.page + 1 : undefined;
}
export default function UserPageRep(t0) {
  const $ = _c(115);
  const {
    profile,
    initialActivityLogParams
  } = t0;
  const params = useParams();
  const t1 = params.user as string;
  let t2;
  if ($[0] !== t1) {
    t2 = t1.toLowerCase();
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const user = t2;
  const {
    connectedProfile,
    activeProfileProxy
  } = useContext(AuthContext);
  const {
    address
  } = useSeizeConnectContext();
  const [repDirection, setRepDirection] = useState("received");
  const [isNicRateOpen, setIsNicRateOpen] = useState(false);
  const [visibleCounts, setVisibleCounts] = useState(getDefaultVisibleCounts);
  const visibleCountsRef = useRef(visibleCounts);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = getDefaultFetchStates();
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const isFetchingNextPageRef = useRef(t3);
  let t4;
  let t5;
  if ($[3] !== visibleCounts) {
    t4 = () => {
      visibleCountsRef.current = visibleCounts;
    };
    t5 = [visibleCounts];
    $[3] = visibleCounts;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  if ($[6] !== activeProfileProxy || $[7] !== address || $[8] !== connectedProfile || $[9] !== profile) {
    t6 = getCanEditNic({
      connectedProfile,
      targetProfile: profile,
      activeProfileProxy,
      address
    });
    $[6] = activeProfileProxy;
    $[7] = address;
    $[8] = connectedProfile;
    $[9] = profile;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const canEditNic = t6;
  let t7;
  let t8;
  if ($[11] !== user) {
    t7 = [QueryKey.REP_OVERVIEW, {
      handleOrWallet: user,
      direction: "incoming"
    }];
    t8 = async () => await commonApiFetch({
      endpoint: `profiles/${user}/rep/overview`
    });
    $[11] = user;
    $[12] = t7;
    $[13] = t8;
  } else {
    t7 = $[12];
    t8 = $[13];
  }
  const t9 = !!user;
  let t10;
  if ($[14] !== t7 || $[15] !== t8 || $[16] !== t9) {
    t10 = {
      queryKey: t7,
      queryFn: t8,
      enabled: t9
    };
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const {
    data: repOverview,
    isFetching: isFetchingOverview
  } = useQuery(t10);
  const t11 = !!user;
  let t12;
  if ($[18] !== t11 || $[19] !== user) {
    t12 = {
      user,
      queryDirection: "incoming",
      enabled: t11
    };
    $[18] = t11;
    $[19] = user;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const repCategoriesQuery = useRepCategories(t12);
  let t13;
  let t14;
  if ($[21] !== user) {
    t13 = [QueryKey.REP_OVERVIEW, {
      handleOrWallet: user,
      direction: "outgoing"
    }];
    t14 = async () => await commonApiFetch({
      endpoint: `profiles/${user}/rep/overview`,
      params: {
        direction: "outgoing"
      }
    });
    $[21] = user;
    $[22] = t13;
    $[23] = t14;
  } else {
    t13 = $[22];
    t14 = $[23];
  }
  const t15 = !!user && repDirection === "given";
  let t16;
  if ($[24] !== t13 || $[25] !== t14 || $[26] !== t15) {
    t16 = {
      queryKey: t13,
      queryFn: t14,
      enabled: t15
    };
    $[24] = t13;
    $[25] = t14;
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  const {
    data: repOverviewGiven,
    isFetching: isFetchingOverviewGiven
  } = useQuery(t16);
  const t17 = !!user && repDirection === "given";
  let t18;
  if ($[28] !== t17 || $[29] !== user) {
    t18 = {
      user,
      queryDirection: "outgoing",
      apiDirection: "outgoing",
      enabled: t17
    };
    $[28] = t17;
    $[29] = user;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  const repCategoriesGivenQuery = useRepCategories(t18);
  let t19;
  let t20;
  if ($[31] !== user) {
    t19 = [QueryKey.CIC_OVERVIEW, {
      handleOrWallet: user
    }];
    t20 = async () => await commonApiFetch({
      endpoint: `profiles/${user}/cic/overview`
    });
    $[31] = user;
    $[32] = t19;
    $[33] = t20;
  } else {
    t19 = $[32];
    t20 = $[33];
  }
  const t21 = !!user;
  let t22;
  if ($[34] !== t19 || $[35] !== t20 || $[36] !== t21) {
    t22 = {
      queryKey: t19,
      queryFn: t20,
      enabled: t21
    };
    $[34] = t19;
    $[35] = t20;
    $[36] = t21;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  const {
    data: cicOverview
  } = useQuery(t22);
  let t23;
  if ($[38] !== repCategoriesQuery.data?.pages) {
    t23 = repCategoriesQuery.data?.pages.flatMap(_temp2) ?? [];
    $[38] = repCategoriesQuery.data?.pages;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  const repCategories = t23;
  let t24;
  if ($[40] !== repCategoriesGivenQuery.data?.pages) {
    t24 = repCategoriesGivenQuery.data?.pages.flatMap(_temp3) ?? [];
    $[40] = repCategoriesGivenQuery.data?.pages;
    $[41] = t24;
  } else {
    t24 = $[41];
  }
  const repCategoriesGiven = t24;
  const activeOverview = repDirection === "received" ? repOverview ?? null : repOverviewGiven ?? null;
  const activeCategories = repDirection === "received" ? repCategories : repCategoriesGiven;
  const activeHasNextPage = repDirection === "received" ? Boolean(repCategoriesQuery.hasNextPage) : Boolean(repCategoriesGivenQuery.hasNextPage);
  const activeIsFetchingNextPage = repDirection === "received" ? repCategoriesQuery.isFetchingNextPage : repCategoriesGivenQuery.isFetchingNextPage;
  const activeVisibleCount = visibleCounts[repDirection];
  const activeLoading = repDirection === "received" ? isFetchingOverview || repCategoriesQuery.isFetching : isFetchingOverviewGiven || repCategoriesGivenQuery.isFetching;
  let t25;
  if ($[42] !== repCategories.length || $[43] !== repCategoriesGiven.length || $[44] !== repCategoriesGivenQuery.fetchNextPage || $[45] !== repCategoriesGivenQuery.hasNextPage || $[46] !== repCategoriesGivenQuery.isFetchingNextPage || $[47] !== repCategoriesQuery.fetchNextPage || $[48] !== repCategoriesQuery.hasNextPage || $[49] !== repCategoriesQuery.isFetchingNextPage || $[50] !== repDirection) {
    t25 = () => {
      const nextVisibleCounts = {
        ...visibleCountsRef.current,
        [repDirection]: visibleCountsRef.current[repDirection] + VISIBLE_CATEGORY_LOAD_STEP
      };
      visibleCountsRef.current = nextVisibleCounts;
      setVisibleCounts(nextVisibleCounts);
      const loadedCount = repDirection === "received" ? repCategories.length : repCategoriesGiven.length;
      const hasNextPage = repDirection === "received" ? Boolean(repCategoriesQuery.hasNextPage) : Boolean(repCategoriesGivenQuery.hasNextPage);
      const isFetchingNextPage = repDirection === "received" ? repCategoriesQuery.isFetchingNextPage : repCategoriesGivenQuery.isFetchingNextPage;
      const fetchNextPage = repDirection === "received" ? repCategoriesQuery.fetchNextPage : repCategoriesGivenQuery.fetchNextPage;
      if (nextVisibleCounts[repDirection] > loadedCount && hasNextPage && !isFetchingNextPage && !isFetchingNextPageRef.current[repDirection]) {
        isFetchingNextPageRef.current[repDirection] = true;
        fetchNextPage().catch(_temp4).finally(() => {
          isFetchingNextPageRef.current[repDirection] = false;
        });
      }
    };
    $[42] = repCategories.length;
    $[43] = repCategoriesGiven.length;
    $[44] = repCategoriesGivenQuery.fetchNextPage;
    $[45] = repCategoriesGivenQuery.hasNextPage;
    $[46] = repCategoriesGivenQuery.isFetchingNextPage;
    $[47] = repCategoriesQuery.fetchNextPage;
    $[48] = repCategoriesQuery.hasNextPage;
    $[49] = repCategoriesQuery.isFetchingNextPage;
    $[50] = repDirection;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  const handleShowMore = t25;
  const t26 = cicOverview ?? null;
  let t27;
  if ($[52] !== activeCategories || $[53] !== activeHasNextPage || $[54] !== activeIsFetchingNextPage || $[55] !== activeLoading || $[56] !== activeOverview || $[57] !== activeVisibleCount || $[58] !== handleShowMore || $[59] !== initialActivityLogParams || $[60] !== profile || $[61] !== repDirection || $[62] !== t26) {
    t27 = <div className="lg:tw-hidden"><UserPageRepMobile profile={profile} overview={activeOverview} categories={activeCategories} cicOverview={t26} repDirection={repDirection} onRepDirectionChange={setRepDirection} initialActivityLogParams={initialActivityLogParams} loading={activeLoading} visibleCount={activeVisibleCount} onShowMore={handleShowMore} hasNextPage={activeHasNextPage} isFetchingNextPage={activeIsFetchingNextPage} /></div>;
    $[52] = activeCategories;
    $[53] = activeHasNextPage;
    $[54] = activeIsFetchingNextPage;
    $[55] = activeLoading;
    $[56] = activeOverview;
    $[57] = activeVisibleCount;
    $[58] = handleShowMore;
    $[59] = initialActivityLogParams;
    $[60] = profile;
    $[61] = repDirection;
    $[62] = t26;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  let t28;
  if ($[64] !== activeCategories || $[65] !== activeHasNextPage || $[66] !== activeIsFetchingNextPage || $[67] !== activeLoading || $[68] !== activeOverview || $[69] !== activeVisibleCount || $[70] !== handleShowMore || $[71] !== profile || $[72] !== repDirection) {
    t28 = <UserPageRepHeader overview={activeOverview} categories={activeCategories} profile={profile} repDirection={repDirection} onRepDirectionChange={setRepDirection} loading={activeLoading} visibleCount={activeVisibleCount} onShowMore={handleShowMore} hasNextPage={activeHasNextPage} isFetchingNextPage={activeIsFetchingNextPage} />;
    $[64] = activeCategories;
    $[65] = activeHasNextPage;
    $[66] = activeIsFetchingNextPage;
    $[67] = activeLoading;
    $[68] = activeOverview;
    $[69] = activeVisibleCount;
    $[70] = handleShowMore;
    $[71] = profile;
    $[72] = repDirection;
    $[73] = t28;
  } else {
    t28 = $[73];
  }
  let t29;
  if ($[74] !== initialActivityLogParams) {
    t29 = <div className="tw-mt-6 lg:tw-mt-8"><UserPageCombinedActivityLog initialActivityLogParams={initialActivityLogParams} /></div>;
    $[74] = initialActivityLogParams;
    $[75] = t29;
  } else {
    t29 = $[75];
  }
  let t30;
  if ($[76] !== t28 || $[77] !== t29) {
    t30 = <div className="tw-min-w-0">{t28}{t29}</div>;
    $[76] = t28;
    $[77] = t29;
    $[78] = t30;
  } else {
    t30 = $[78];
  }
  let t31;
  let t32;
  let t33;
  let t34;
  let t35;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-bg-gradient-to-br tw-from-emerald-500/[0.05] tw-via-transparent tw-to-transparent" />;
    t32 = <div className="tw-absolute tw-left-0 tw-right-0 tw-top-0 tw-h-px tw-bg-gradient-to-r tw-from-transparent tw-via-emerald-400/25 tw-to-transparent" />;
    t33 = <div className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-h-px tw-bg-gradient-to-r tw-from-transparent tw-via-emerald-400/40 tw-to-transparent" />;
    t34 = <div className="tw-absolute tw-bottom-0 tw-left-0 tw-top-0 tw-w-px tw-bg-gradient-to-b tw-from-transparent tw-via-emerald-400/20 tw-to-transparent" />;
    t35 = <div className="tw-absolute tw-bottom-0 tw-right-0 tw-top-0 tw-w-px tw-bg-gradient-to-b tw-from-transparent tw-via-emerald-400/20 tw-to-transparent" />;
    $[79] = t31;
    $[80] = t32;
    $[81] = t33;
    $[82] = t34;
    $[83] = t35;
  } else {
    t31 = $[79];
    t32 = $[80];
    t33 = $[81];
    t34 = $[82];
    t35 = $[83];
  }
  const t36 = cicOverview ?? null;
  let t37;
  if ($[84] !== canEditNic) {
    t37 = canEditNic ? {
      onRateClick: () => setIsNicRateOpen(true)
    } : {};
    $[84] = canEditNic;
    $[85] = t37;
  } else {
    t37 = $[85];
  }
  let t38;
  if ($[86] !== profile || $[87] !== t36 || $[88] !== t37) {
    t38 = <UserPageIdentityHeader profile={profile} cicOverview={t36} {...t37} />;
    $[86] = profile;
    $[87] = t36;
    $[88] = t37;
    $[89] = t38;
  } else {
    t38 = $[89];
  }
  let t39;
  if ($[90] !== profile) {
    t39 = <UserPageIdentityStatements profile={profile} />;
    $[90] = profile;
    $[91] = t39;
  } else {
    t39 = $[91];
  }
  let t40;
  if ($[92] !== t38 || $[93] !== t39) {
    t40 = <div className="tw-min-w-0 tw-self-start"><div className="tw-relative tw-overflow-hidden tw-rounded-2xl tw-border tw-border-solid tw-border-white/[0.08] tw-bg-[#08090b]">{t31}{t32}{t33}{t34}{t35}<div className="tw-relative tw-z-10">{t38}{t39}</div></div></div>;
    $[92] = t38;
    $[93] = t39;
    $[94] = t40;
  } else {
    t40 = $[94];
  }
  let t41;
  if ($[95] !== t30 || $[96] !== t40) {
    t41 = <div className="tw-hidden lg:tw-block"><div className="tw-grid tw-grid-cols-1 tw-gap-x-8 lg:tw-grid-cols-[minmax(0,2fr)_minmax(22rem,1fr)] xl:tw-gap-x-10">{t30}{t40}</div></div>;
    $[95] = t30;
    $[96] = t40;
    $[97] = t41;
  } else {
    t41 = $[97];
  }
  let t42;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = () => setIsNicRateOpen(false);
    $[98] = t42;
  } else {
    t42 = $[98];
  }
  let t43;
  if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = () => setIsNicRateOpen(false);
    $[99] = t43;
  } else {
    t43 = $[99];
  }
  let t44;
  if ($[100] !== profile) {
    t44 = <UserPageIdentityHeaderCICRate profile={profile} isTooltip={false} onSuccess={t43} />;
    $[100] = profile;
    $[101] = t44;
  } else {
    t44 = $[101];
  }
  let t45;
  if ($[102] !== profile || $[103] !== t44) {
    t45 = <UserPageRateWrapper profile={profile} type={RateMatter.NIC}>{t44}</UserPageRateWrapper>;
    $[102] = profile;
    $[103] = t44;
    $[104] = t45;
  } else {
    t45 = $[104];
  }
  let t46;
  if ($[105] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = <div className="tw-mt-3"><button onClick={() => setIsNicRateOpen(false)} type="button" className="tw-w-full tw-cursor-pointer tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-px-4 tw-py-3 tw-text-sm tw-font-semibold tw-text-white tw-transition tw-duration-300 tw-ease-out hover:tw-bg-iron-800">Cancel</button></div>;
    $[105] = t46;
  } else {
    t46 = $[105];
  }
  let t47;
  if ($[106] !== t45) {
    t47 = <div className="tw-px-4 sm:tw-px-6">{t45}{t46}</div>;
    $[106] = t45;
    $[107] = t47;
  } else {
    t47 = $[107];
  }
  let t48;
  if ($[108] !== isNicRateOpen || $[109] !== t47) {
    t48 = <MobileWrapperDialog title="Rate NIC" isOpen={isNicRateOpen} onClose={t42} tabletModal={true} maxWidthClass="md:tw-max-w-md">{t47}</MobileWrapperDialog>;
    $[108] = isNicRateOpen;
    $[109] = t47;
    $[110] = t48;
  } else {
    t48 = $[110];
  }
  let t49;
  if ($[111] !== t27 || $[112] !== t41 || $[113] !== t48) {
    t49 = <div className="tailwind-scope">{t27}{t41}{t48}</div>;
    $[111] = t27;
    $[112] = t41;
    $[113] = t48;
    $[114] = t49;
  } else {
    t49 = $[114];
  }
  return t49;
}
function _temp4() {}
function _temp3(page_0) {
  return page_0.data;
}
function _temp2(page) {
  return page.data;
}